import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, Users, Activity, RefreshCw, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import api from '../../utils/api';

// Usage Analytics multiclient (GH#29 / task #29). Rehecho desde cero sobre el
// panel previo: agrega selector de CLIENTE + rango y responde las 3 preguntas
// de negocio B2B: qué usuarios de cada cliente usan la plataforma, con qué
// frecuencia y qué secciones. Consume GET /api/activity/stats (MariaDB
// activity_events, GH#21). Ver docs/pendientes/refactor-stats-usage-analytics-2026-08-09.md
interface ActivityStats {
  range: string;
  summary: {
    activeUsers: number;
    logins: number;
    pageviews: number;
    actions: number;
  };
  byDay: { date: string; count: number }[];
  topUsers: { email: string; count: number; lastSeen: string | null }[];
  topPages: { page: string; count: number }[];
  // bySection: uso por sección (derivado de `page` en el backend — activity_events
  // NO tiene columna `section`, se mapea page→sección). Ver gap documentado.
  bySection?: { section: string; count: number; users: number }[];
  recent: {
    email: string | null;
    event_type: string;
    page: string | null;
    action: string | null;
    created_at: string | null;
  }[];
}

const EMPTY_STATS: ActivityStats = {
  range: '30d',
  summary: { activeUsers: 0, logins: 0, pageviews: 0, actions: 0 },
  byDay: [],
  topUsers: [],
  topPages: [],
  bySection: [],
  recent: [],
};

interface ClientOption {
  uid: string;
  name: string;
}

// Formato fecha es-AR DD/MM/YYYY HH:mm (24h). Ver convención global.
function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Etiqueta corta del día para el eje del gráfico (DD/MM).
function formatDayShort(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

const nf = (n: number) => n.toLocaleString('es-AR');

const RANGE_OPTIONS = [
  { value: '7d', label: 'Últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
];

const UsageStats: React.FC = () => {
  const [stats, setStats] = useState<ActivityStats>(EMPTY_STATS);
  const [range, setRange] = useState('30d');
  const [clientId, setClientId] = useState('all');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lista de clientes: mismo patrón que ClientsManagement/UsersSection
  // (Firestore `clients`). Reusamos la fuente única de la lista de clientes.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'clients'));
        const list: ClientOption[] = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.active === false) return;
          list.push({ uid: docSnap.id, name: data.name || docSnap.id });
        });
        list.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        setClients(list);
      } catch (err) {
        console.error('Error cargando lista de clientes:', err);
      }
    })();
  }, []);

  const fetchStats = useCallback(async (rangeVal: string, clientVal: string) => {
    setLoading(true);
    setError(null);
    try {
      // Para un cliente concreto seteamos x-client-id explícito → el backend
      // resuelve la DB de ESE cliente. Para 'all', el backend itera todas las
      // DBs de clientes activos (no necesita el header).
      const config: any = { params: { range: rangeVal, clientId: clientVal } };
      if (clientVal && clientVal !== 'all') {
        config.headers = { 'x-client-id': clientVal };
      }
      const resp = await api.get('/api/activity/stats', config);
      setStats(resp.data || EMPTY_STATS);
    } catch (err: any) {
      setError(err?.message || 'Error cargando estadísticas de actividad');
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(range, clientId);
  }, [range, clientId, fetchStats]);

  const chartData = stats.byDay.map((d) => ({ date: formatDayShort(d.date), count: d.count }));
  const sectionData = (stats.bySection && stats.bySection.length > 0)
    ? stats.bySection
    : stats.topPages.map((p) => ({ section: p.page, count: p.count, users: 0 }));

  const hasAnyData =
    stats.summary.activeUsers > 0 ||
    stats.byDay.length > 0 ||
    stats.topUsers.length > 0 ||
    sectionData.length > 0;

  return (
    <div className="space-y-4">
      {/* Header + controles (un solo set de controles primario) */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-800">Uso de la plataforma</h3>
        </div>

        <div className="flex items-center gap-2">
          {/* Selector de cliente */}
          <div className="flex items-center gap-1.5">
            <Building2 className="h-4 w-4 text-gray-400" />
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los clientes</option>
              {clients.map((c) => (
                <option key={c.uid} value={c.uid}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Selector de rango */}
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          <button
            onClick={() => fetchStats(range, clientId)}
            disabled={loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}

      {/* Empty-state limpio cuando aún no hay captura de actividad (GH#21) */}
      {!hasAnyData && !loading && !error ? (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <Activity className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-700">Sin datos de actividad todavía</p>
          <p className="mt-1 text-sm text-gray-400">
            {clientId === 'all'
              ? 'No hay eventos registrados en el rango seleccionado.'
              : 'Este cliente no registró actividad en el rango seleccionado.'}
          </p>
        </div>
      ) : (
        <>
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Usuarios activos</p>
                  <p className="text-2xl font-bold text-blue-700 tabular-nums">{nf(stats.summary.activeUsers)}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Logins</p>
                  <p className="text-2xl font-bold text-green-700 tabular-nums">{nf(stats.summary.logins)}</p>
                </div>
                <Activity className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Navegación</p>
                  <p className="text-2xl font-bold text-purple-700 tabular-nums">{nf(stats.summary.pageviews)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Bloque 1 — Actividad por día + Secciones */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Actividad por día</h4>
              <div className="h-56">
                {chartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-400">
                    Sin datos de actividad todavía
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Secciones más usadas</h4>
              <div className="h-56">
                {sectionData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-400">
                    Sin datos de actividad todavía
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sectionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="section" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Bloque 2 — Usuarios por cliente (email / eventos / último acceso) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Usuarios activos</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eventos</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último acceso</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.topUsers.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-4 text-sm text-gray-400 text-center">
                        Sin datos de actividad todavía
                      </td>
                    </tr>
                  ) : (
                    stats.topUsers.map((u, i) => (
                      <tr key={`${u.email}-${i}`}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{u.email}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm tabular-nums">{nf(u.count)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 tabular-nums">{formatDate(u.lastSeen)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UsageStats;
